import { useEffect, useState } from 'react';
import { FileTextOutlined, PrinterOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Segmented, Tag } from 'antd';
import { api } from '../api';
import { Bone, DataTable, ErrorNotice, PageHeader, Panel, Person, RowActions } from '../components';
import { countLabel, formatDate, formatDateTime, formatRupees, formatRupeesExact, timeAgo, useLoad } from '../lib';
const STATUS = {
    paid: { color: 'success', label: 'Paid' },
    failed: { color: 'error', label: 'Failed' },
    created: { color: 'default', label: 'Not completed' }
};
export function PaymentStatusTag({ status }) {
    const tag = STATUS[status];
    return (<Tag color={tag.color} variant="filled">
      {status === 'paid' && <span className="status-dot"/>}
      {tag.label}
    </Tag>);
}
export function PaymentsPage() {
    const [filter, setFilter] = useState('all');
    const [invoiceFor, setInvoiceFor] = useState(null);
    const { data, error, loading, reload } = useLoad(() => api('/v1/admin/payments').then((body) => body.payments));
    const payments = data ?? [];
    const shown = payments.filter((row) => filter === 'all' || row.status === filter);
    const count = (status) => payments.filter((row) => row.status === status).length;
    return (<>
      <PageHeader title="Payments" description="Plans bought through Razorpay in the account panel, newest first. A paid payment starts or extends the customer's subscription and gets an invoice." actions={<Button icon={<ReloadOutlined />} onClick={() => void reload()} loading={loading}>
            Refresh
          </Button>}/>
      <ErrorNotice error={error}/>

      <Panel flush>
        <div className="toolbar">
          <Segmented value={filter} onChange={setFilter} options={[
            { value: 'all', label: countLabel('All', payments.length, data !== null) },
            { value: 'paid', label: countLabel('Paid', count('paid'), data !== null) },
            { value: 'failed', label: countLabel('Failed', count('failed'), data !== null) },
            { value: 'created', label: countLabel('Not completed', count('created'), data !== null) }
        ]}/>
        </div>
        <DataTable rowKey="id" loading={loading} dataSource={shown} pagination={shown.length > 20 ? { pageSize: 20, showSizeChanger: false } : false} scroll={{ x: 1240 }} locale={{ emptyText: payments.length ? 'No payments match.' : 'No payments yet. They appear here when a customer buys a plan.' }} columns={[
            {
                title: 'Date',
                dataIndex: 'createdAt',
                width: 170,
                skeleton: 'lines',
                render: (_value, row) => (<span>
                  {timeAgo(row.paidAt ?? row.createdAt)}
                  <span className="cell-sub num">{formatDateTime(row.paidAt ?? row.createdAt)}</span>
                </span>)
            },
            { title: 'Customer', skeleton: 'person', render: (_value, row) => <Person name={row.accountName} detail={<span className="num">{row.accountMobile ?? '—'}</span>}/> },
            { title: 'Plan', dataIndex: 'planName', width: 130, render: (value, row) => (<span>{value}{row.planKind && <span className="cell-sub">{row.planKind === 'renewal' ? 'Renewal' : 'Activation'}</span>}</span>) },
            { title: 'Amount', dataIndex: 'amountPaise', width: 120, align: 'right', render: (value) => <span className="num">{formatRupees(value)}</span> },
            {
                title: 'Status',
                dataIndex: 'status',
                width: 150,
                skeleton: 'tag',
                render: (value, row) => (<span>
                  <PaymentStatusTag status={value}/>
                  {row.failureReason && <span className="cell-sub">{row.failureReason}</span>}
                </span>)
            },
            { title: 'Invoice', dataIndex: 'invoiceNumber', width: 160, render: (value) => (value ? <span className="mono">{value}</span> : <span className="cell-sub">—</span>) },
            {
                title: 'Razorpay payment',
                dataIndex: 'razorpayPaymentId',
                width: 200,
                skeleton: 'lines',
                render: (value, row) => (<span>
                  <span className="mono">{value ?? '—'}</span>
                  <span className="cell-sub mono">{row.razorpayOrderId}</span>
                </span>)
            },
            {
                title: 'Period bought',
                width: 210,
                render: (_value, row) => row.validFrom && row.validUntil ? (<span>
                    {formatDate(row.validFrom)} – {formatDate(row.validUntil)}
                  </span>) : (<span className="cell-sub">—</span>)
            },
            {
                title: '',
                width: 56,
                align: 'right',
                skeleton: 'actions',
                render: (_value, row) => (<RowActions label={`Actions for payment by ${row.accountName}`} actions={[
                        {
                            key: 'invoice',
                            icon: <FileTextOutlined />,
                            label: 'View invoice',
                            disabled: row.status !== 'paid',
                            hint: row.status === 'paid' ? row.invoiceNumber : 'Only a paid payment has an invoice',
                            onClick: () => setInvoiceFor(row)
                        }
                    ]}/>)
            }
        ]}/>
      </Panel>

      <InvoiceDialog payment={invoiceFor} onClose={() => setInvoiceFor(null)}/>
    </>);
}
/** A paid payment's tax invoice, as the customer gets it, with a Print button (prints only the invoice). */
function InvoiceDialog({ payment, onClose }) {
    const [invoice, setInvoice] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        setInvoice(null);
        setError(null);
        if (!payment)
            return;
        let live = true;
        api(`/v1/admin/payments/${payment.id}/invoice`)
            .then((body) => live && setInvoice(body.invoice))
            .catch((err) => live && setError(err instanceof Error ? err.message : 'Could not load the invoice'));
        return () => {
            live = false;
        };
    }, [payment]);
    function print() {
        // Hides the console behind the dialog while the browser prints (see .print-invoice in styles.css).
        document.body.classList.add('print-invoice');
        const done = () => {
            document.body.classList.remove('print-invoice');
            window.removeEventListener('afterprint', done);
        };
        window.addEventListener('afterprint', done);
        window.print();
    }
    const local = invoice ? invoice.igstPaise === 0 : true;
    return (<Modal title={invoice ? `Invoice ${invoice.number}` : 'Invoice'} open={Boolean(payment)} onCancel={onClose} width={720} destroyOnHidden footer={[
            <Button key="close" onClick={onClose}>
          Close
        </Button>,
            <Button key="print" type="primary" icon={<PrinterOutlined />} disabled={!invoice} onClick={print}>
          Print
        </Button>
        ]}>
      {error && <Alert type="error" showIcon title={error}/>}
      {!invoice && !error && (<div className="bone-lines" style={{ gap: 14 }} aria-busy>
          <Bone width="40%" height={16}/>
          <Bone width="70%" height={11}/>
          <Bone width="60%" height={11}/>
          <Bone height={80} style={{ borderRadius: 8 }}/>
          <Bone width="35%" height={14} style={{ marginLeft: 'auto' }}/>
        </div>)}
      {invoice && (<div className="invoice">
          <div className="invoice-head">
            <div>
              <h2>Tax invoice</h2>
              <span className="cell-sub">
                No. <b className="mono">{invoice.number}</b> · {formatDate(invoice.date)}
              </span>
            </div>
          </div>
          <div className="invoice-parties">
            <div>
              <span className="invoice-label">From</span>
              <strong>{invoice.seller.name}</strong>
              {invoice.seller.address && <span>{invoice.seller.address}</span>}
              {invoice.seller.state && <span>{invoice.seller.state}</span>}
              {invoice.seller.gstin && <span className="mono">GSTIN {invoice.seller.gstin}</span>}
            </div>
            <div>
              <span className="invoice-label">Bill to</span>
              <strong>{invoice.buyer.name}</strong>
              {invoice.buyer.address && <span>{invoice.buyer.address}</span>}
              {invoice.buyer.state && <span>{invoice.buyer.state}</span>}
              {invoice.buyer.gstin && <span className="mono">GSTIN {invoice.buyer.gstin}</span>}
              {invoice.buyer.mobile && <span className="num">{invoice.buyer.mobile}</span>}
              {invoice.buyer.email && <span>{invoice.buyer.email}</span>}
            </div>
          </div>
          <table className="invoice-lines">
            <thead>
              <tr>
                <th>Description</th>
                <th className="amount">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{invoice.line.description}</td>
                <td className="amount num">{formatRupeesExact(invoice.taxablePaise)}</td>
              </tr>
            </tbody>
            <tfoot>
              {invoice.discountPaise > 0 && (<>
                  <tr>
                    <td>MRP</td>
                    <td className="amount num">{formatRupeesExact(invoice.mrpPaise)}</td>
                  </tr>
                  <tr>
                    <td>Discount</td>
                    <td className="amount num">− {formatRupeesExact(invoice.discountPaise)}</td>
                  </tr>
                </>)}
              <tr>
                <td>Taxable value</td>
                <td className="amount num">{formatRupeesExact(invoice.taxablePaise)}</td>
              </tr>
              {local ? (<>
                  <tr>
                    <td>CGST @ {invoice.gstRate / 2}%</td>
                    <td className="amount num">{formatRupeesExact(invoice.cgstPaise)}</td>
                  </tr>
                  <tr>
                    <td>SGST @ {invoice.gstRate / 2}%</td>
                    <td className="amount num">{formatRupeesExact(invoice.sgstPaise)}</td>
                  </tr>
                </>) : (<tr>
                  <td>IGST @ {invoice.gstRate}%</td>
                  <td className="amount num">{formatRupeesExact(invoice.igstPaise)}</td>
                </tr>)}
              <tr className="invoice-total">
                <td>Total</td>
                <td className="amount num">{formatRupeesExact(invoice.totalPaise)}</td>
              </tr>
            </tfoot>
          </table>
          <p className="cell-sub" style={{ marginTop: 14 }}>
            Paid online through Razorpay on {formatDateTime(invoice.payment.paidAt)} · payment <span className="mono">{invoice.payment.razorpayPaymentId || '—'}</span>
          </p>
        </div>)}
    </Modal>);
}
